/**
 * =============================================================================
 * Service Accounts Page — FE issue #28 / BE #64
 * =============================================================================
 *
 * system_admin-only management of machine identities used by automated
 * monitoring (role "monitor"): list, create, rotate token, deactivate and
 * activate. There is intentionally no delete (PROD rule: nothing is deleted).
 *
 * Token handling: the plaintext token (`rpsa_<prefix>_<secret>`) is returned
 * only by create/rotate. It lives solely in the one-time token modal's state
 * and is dropped (state cleared, mutation results reset with gcTime 0) as soon
 * as the modal closes. It is never logged, stored in browser storage or put
 * in the URL.
 */

import React, { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DataTable, { Column } from '../components/DataTable'
import Modal from '../components/Modal'
import { serviceAccountsApi, type ServiceAccount } from '../services/api'
import { getLang } from '../utils/promotionI18n'
import {
  SERVICE_ACCOUNT_NAME_RE,
  sat,
  serviceAccountErrorMessage,
} from '../utils/serviceAccountsI18n'

// =============================================================================
// Helpers
// =============================================================================

const QUERY_KEY = ['service-accounts'] as const

type ConfirmKind = 'rotate' | 'deactivate' | 'activate'

interface ConfirmState {
  kind: ConfirmKind
  account: ServiceAccount
}

/** One-time token reveal; cleared on close so the secret is not retained. */
interface RevealState {
  kind: 'created' | 'rotated'
  name: string
  token: string
}

const formatDateTime = (value: string | null | undefined): string | null => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString(getLang() === 'sk' ? 'sk-SK' : 'en-GB')
}

// =============================================================================
// Component
// =============================================================================

const ServiceAccountsPage: React.FC = () => {
  const queryClient = useQueryClient()

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [form, setForm] = useState({ name: '', description: '' })
  const [formError, setFormError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<ConfirmState | null>(null)
  const [reveal, setReveal] = useState<RevealState | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const { data: accounts = [], isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => (await serviceAccountsApi.list()).data,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  // gcTime 0: the mutation (whose result contains the token) is dropped from
  // the react-query mutation cache as soon as it has no observer/result.
  const createMutation = useMutation({
    mutationFn: async (payload: { name: string; description: string | null }) =>
      (await serviceAccountsApi.create(payload)).data,
    gcTime: 0,
    onSuccess: (result) => {
      setIsCreateOpen(false)
      setForm({ name: '', description: '' })
      setFormError(null)
      setCopyState('idle')
      setReveal({ kind: 'created', name: result.account.name, token: result.token })
      invalidate()
    },
    onError: (err) => setFormError(serviceAccountErrorMessage(err)),
  })

  const rotateMutation = useMutation({
    mutationFn: async (account: ServiceAccount) => (await serviceAccountsApi.rotate(account.id)).data,
    gcTime: 0,
    onSuccess: (result) => {
      setConfirm(null)
      setCopyState('idle')
      setReveal({ kind: 'rotated', name: result.account.name, token: result.token })
      invalidate()
    },
    onError: (err) => {
      setConfirm(null)
      setError(serviceAccountErrorMessage(err))
    },
  })

  const statusMutation = useMutation({
    mutationFn: async ({ account, active }: { account: ServiceAccount; active: boolean }) =>
      (active
        ? await serviceAccountsApi.activate(account.id)
        : await serviceAccountsApi.deactivate(account.id)
      ).data,
    onSuccess: (updated, { active }) => {
      setConfirm(null)
      setSuccess(sat(active ? 'activatedOk' : 'deactivatedOk', { name: updated.name }))
      invalidate()
    },
    onError: (err) => {
      setConfirm(null)
      setError(serviceAccountErrorMessage(err))
    },
  })

  const { reset: resetCreate } = createMutation
  const { reset: resetRotate } = rotateMutation

  /** Close the one-time token modal and forget the token everywhere. */
  const closeReveal = useCallback(() => {
    setReveal(null)
    setCopyState('idle')
    resetCreate()
    resetRotate()
  }, [resetCreate, resetRotate])

  const copyToken = async () => {
    if (!reveal) return
    try {
      await navigator.clipboard.writeText(reveal.token)
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  const openCreate = () => {
    setForm({ name: '', description: '' })
    setFormError(null)
    setError(null)
    setSuccess(null)
    setIsCreateOpen(true)
  }

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault()
    const name = form.name.trim()
    if (!SERVICE_ACCOUNT_NAME_RE.test(name)) {
      setFormError(sat('fieldNameInvalid'))
      return
    }
    const description = form.description.trim()
    createMutation.mutate({ name, description: description || null })
  }

  const askConfirm = (kind: ConfirmKind, account: ServiceAccount) => {
    setError(null)
    setSuccess(null)
    setConfirm({ kind, account })
  }

  const runConfirm = () => {
    if (!confirm) return
    if (confirm.kind === 'rotate') {
      rotateMutation.mutate(confirm.account)
    } else {
      statusMutation.mutate({ account: confirm.account, active: confirm.kind === 'activate' })
    }
  }

  const actionPending = rotateMutation.isPending || statusMutation.isPending

  // ---------------------------------------------------------------------------
  // Table
  // ---------------------------------------------------------------------------

  const columns: Column<ServiceAccount>[] = useMemo(
    () => [
      {
        key: 'name',
        header: sat('colName'),
        accessor: 'name',
        sortable: true,
        searchable: true,
        render: (a) => <span className="font-mono font-medium text-gray-900">{a.name}</span>,
      },
      {
        key: 'description',
        header: sat('colDescription'),
        accessor: 'description',
        searchable: true,
        render: (a) => <span className="text-sm text-gray-600">{a.description || '—'}</span>,
      },
      {
        key: 'role',
        header: sat('colRole'),
        render: () => (
          <div className="flex flex-col items-start gap-1">
            <span className="badge bg-blue-100 text-blue-800">{sat('roleMonitor')}</span>
            <span className="badge bg-gray-100 text-gray-700">{sat('typeServiceAccount')}</span>
          </div>
        ),
      },
      {
        key: 'status',
        header: sat('colStatus'),
        sortable: true,
        sortFn: (a, b) => Number(a.is_active) - Number(b.is_active),
        render: (a) => (
          <span className={`badge ${a.is_active ? 'badge-active' : 'badge-inactive'}`}>
            {a.is_active ? sat('statusActive') : sat('statusInactive')}
          </span>
        ),
      },
      {
        key: 'token_prefix',
        header: sat('colTokenPrefix'),
        render: (a) => (
          <code className="text-xs text-gray-700" data-testid={`token-prefix-${a.id}`}>
            rpsa_{a.token_prefix}_…
          </code>
        ),
      },
      {
        key: 'created_at',
        header: sat('colCreated'),
        sortable: true,
        accessor: 'created_at',
        render: (a) => (
          <div className="text-xs text-gray-600">
            <div>{formatDateTime(a.created_at) ?? '—'}</div>
            {a.created_by && <div>{sat('createdBy', { who: a.created_by })}</div>}
          </div>
        ),
      },
      {
        key: 'rotated_at',
        header: sat('colRotated'),
        render: (a) => <span className="text-xs text-gray-600">{formatDateTime(a.rotated_at) ?? '—'}</span>,
      },
      {
        key: 'last_used_at',
        header: sat('colLastUsed'),
        render: (a) => (
          <div className="text-xs text-gray-600">
            <div>{formatDateTime(a.last_used_at) ?? sat('never')}</div>
            {a.last_used_ip && <div>{sat('fromIp', { ip: a.last_used_ip })}</div>}
          </div>
        ),
      },
      {
        key: 'actions',
        header: sat('colActions'),
        render: (a) => (
          <div className="flex flex-wrap gap-2">
            <button
              className="btn-secondary px-2 py-1 text-xs"
              data-testid={`rotate-${a.id}`}
              disabled={actionPending || !a.is_active}
              onClick={() => askConfirm('rotate', a)}
            >
              {sat('rotate')}
            </button>
            <button
              className={`${a.is_active ? 'btn-danger' : 'btn-secondary'} px-2 py-1 text-xs`}
              data-testid={`toggle-status-${a.id}`}
              disabled={actionPending}
              onClick={() => askConfirm(a.is_active ? 'deactivate' : 'activate', a)}
            >
              {a.is_active ? sat('deactivate') : sat('activate')}
            </button>
          </div>
        ),
      },
    ],
    [actionPending],
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const confirmTexts = confirm
    ? {
        title: sat(`${confirm.kind}Title`, { name: confirm.account.name }),
        body: sat(`${confirm.kind}Body`),
        action: sat(`${confirm.kind}Confirm`),
      }
    : null

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="max-w-3xl">
          <h2 className="text-lg font-semibold text-gray-900">{sat('pageTitle')}</h2>
          <p className="text-sm text-gray-600" data-testid="service-accounts-intro">
            {sat('intro')}
          </p>
          <p className="mt-1 text-xs text-gray-500">{sat('noDeleteHint')}</p>
        </div>
        <button className="btn-primary" onClick={openCreate} data-testid="open-create-service-account">
          {sat('addAccount')}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}
      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {sat('loadFailed')}
        </div>
      )}

      <DataTable
        columns={columns}
        data={accounts}
        loading={isLoading}
        getRowKey={(a) => a.id}
        emptyMessage={sat('emptyList')}
        searchable
        searchPlaceholder={sat('searchPlaceholder')}
      />

      {/* Create */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title={sat('createTitle')}>
        <form onSubmit={submitCreate} className="space-y-4" noValidate>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sa-name">
              {sat('fieldName')}
            </label>
            <input
              id="sa-name"
              className="input font-mono"
              data-testid="sa-name"
              autoComplete="off"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={63}
              required
            />
            <p className="mt-1 text-xs text-gray-500">{sat('fieldNameHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="sa-description">
              {sat('fieldDescription')}
            </label>
            <textarea
              id="sa-description"
              className="input"
              data-testid="sa-description"
              rows={2}
              placeholder={sat('fieldDescriptionPlaceholder')}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
              {formError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setIsCreateOpen(false)}>
              {sat('cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              data-testid="sa-create-submit"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? sat('creating') : sat('create')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirm rotate / deactivate / activate */}
      <Modal
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirmTexts?.title ?? ''}
      >
        {confirm && confirmTexts && (
          <div className="space-y-4" data-testid={`confirm-${confirm.kind}`}>
            <p
              className={`rounded-lg border px-3 py-2 text-sm ${
                confirm.kind === 'activate'
                  ? 'border-gray-200 bg-gray-50 text-gray-700'
                  : 'border-amber-300 bg-amber-50 text-amber-900'
              }`}
            >
              {confirmTexts.body}
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setConfirm(null)}>
                {sat('cancel')}
              </button>
              <button
                type="button"
                className={confirm.kind === 'activate' ? 'btn-primary' : 'btn-danger'}
                data-testid="confirm-action"
                disabled={actionPending}
                onClick={runConfirm}
              >
                {actionPending ? sat('working') : confirmTexts.action}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* One-time token reveal */}
      <Modal
        isOpen={reveal !== null}
        onClose={closeReveal}
        title={reveal?.kind === 'rotated' ? sat('tokenRotatedTitle') : sat('tokenCreatedTitle')}
        size="lg"
        closeOnBackdrop={false}
      >
        {reveal && (
          <div className="space-y-4" data-testid="token-reveal">
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-900"
              role="alert"
            >
              {sat('tokenWarning')}
            </div>
            {reveal.kind === 'rotated' && <p className="text-sm text-gray-700">{sat('tokenRotatedHint')}</p>}
            <p className="text-sm text-gray-700">{sat('tokenFor', { name: reveal.name })}</p>
            <div className="flex gap-2">
              <code
                className="flex-1 select-all break-all rounded border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm"
                data-testid="token-value"
              >
                {reveal.token}
              </code>
              <button type="button" className="btn-secondary" data-testid="token-copy" onClick={copyToken}>
                {copyState === 'copied' ? sat('copied') : sat('copy')}
              </button>
            </div>
            {copyState === 'failed' && <p className="text-xs text-red-700">{sat('copyFailed')}</p>}
            <div className="flex justify-end">
              <button type="button" className="btn-primary" data-testid="token-close" onClick={closeReveal}>
                {sat('tokenSavedClose')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default ServiceAccountsPage
